import { z } from 'zod'
import set from 'lodash.set'
import get from 'lodash.get'

export type FormDataWithFileHandled<T> = {
  [k in keyof T]: T[k] extends File ? string : T[k] extends File[] ? string[] : T[k]
}

export type ParseFormDataResult<T> =
  | (z.core.util.SafeParseError<T> & { error: { data: T }; formData: FormData; formId: string | null })
  | (z.core.util.SafeParseSuccess<FormDataWithFileHandled<T>> & { formData: FormData; formId: string | null })

export interface ParseFormDataOptions<T> {
  handleFile?(file: File, path: string, data: T): Promise<string>
  errorMap?: z.core.$ZodErrorMap
}

export function parseFormDataClientSide<T>(
  schema: z.core.$ZodType<T>,
  formData: FormData,
  options?: ParseFormDataOptions<T>
) {
  const formId = formData.get('__formId') as string | null

  const flat = flattenZodSchema(schema)
  const booleans = flat.filter((f) => unwrapEffects(f.schema) instanceof z.ZodBoolean)
  const arrays = flat
    .filter(
      (f) =>
        unwrapEffects(f.schema) instanceof z.ZodArray &&
        !z.safeParse(f.schema, null).success &&
        !z.safeParse(f.schema, undefined).success
    )
    .filter((f) => !f.path.includes('.*.'))
  const rawEntries = [...arrays.map((b) => b.path), ...new Set(formData.keys())]
    .map((key) => {
      const schema = resolveSchemaForFormKey(flat, key, formData)
      return [key, parseFormValue(schema, formData.getAll(key))] as const
    })
    .filter(Boolean)

  const { entries, fileKeys } = resolveFileKeys(rawEntries)
  const obj: { [key: string]: any } = {}
  entries.forEach(([key, value]) => set(obj, key, value))
  booleans.forEach((p) => {
    function resolvePath(path: string): string[] {
      if (path.includes('.*.')) {
        const length = get(obj, path.slice(0, path.indexOf('.*.')), []).length
        const indices = Array.from({ length }).map((_, i) => i)
        const newPaths = indices.map((i) => path.replace('.*.', `.${i}.`))
        return newPaths.flatMap(resolvePath)
      } else {
        return [path]
      }
    }
    const paths = resolvePath(p.path)
    paths.forEach((path) => {
      if (get(obj, path) === undefined && !hasExplicitlyNullAncestor(obj, path)) {
        set(obj, path, false)
      }
    })
  })
  const fileEntries = fileKeys.map((key) => [key, get(obj, key)] as const)
  fileKeys.forEach((f) => set(obj, f, 'https://fakeurlforformuploading.com'))
  const safeParsed = z.safeParse(schema, obj, {
    error: options?.errorMap
  })
  if (!safeParsed.success) {
    console.log([...formData.entries()].map((e) => [e[0], e[1]]))
    console.log(entries)
    console.log(obj)

    return {
      ...safeParsed,
      error: Object.assign(safeParsed.error, { data: obj as T }),
      formData,
      formId
    }
  } else {
    return { ...safeParsed, fileEntries, formData, formId }
  }
}

export async function parseFormData<T>(
  schema: z.core.$ZodType<T>,
  formData: FormData | Promise<FormData>,
  options?: ParseFormDataOptions<T>
): Promise<ParseFormDataResult<T>> {
  const data = await formData
  const formId = data.get('__formId') as string | null

  const parsed1 = parseFormDataClientSide(schema, data, options)
  if (!parsed1.success) {
    return { ...parsed1, formData: data }
  } else if (options?.handleFile || !parsed1.fileEntries.length) {
    const handleFile = options?.handleFile as (file: File, path: string, data?: T) => Promise<string>

    const uploadedFiles = await Promise.all(
      parsed1.fileEntries.map(async ([fileKey, file]) => ({
        key: fileKey,
        value: await handleFile(file, fileKey, parsed1.data)
      }))
    )
    const obj2 = { ...parsed1.data } as { [key: string]: any }
    uploadedFiles.forEach(({ key, value }) => set(obj2, key, value))
    const safeParsed2 = z.safeParse(schema, obj2, { error: options?.errorMap })

    if (safeParsed2.success) {
      return { ...safeParsed2, formData: data, formId } as z.core.util.SafeParseSuccess<FormDataWithFileHandled<T>> & {
        formData: FormData
        formId: string | null
      }
    } else {
      console.log(obj2)
      console.log('fileEntries', parsed1.fileEntries)
      return {
        ...safeParsed2,
        error: Object.assign(safeParsed2.error, { data: obj2 as T }),
        formData: data,
        formId
      }
    }
  } else {
    throw new Error('A file was submitted but no file handler was provider')
  }
}

/**
 * A nested boolean is back-filled to `false` when its input is absent, because an unchecked checkbox submits
 * nothing. But `set` replaces any non-object it walks through, so back-filling `customer.transactionalSMSOptIn`
 * on a `{ customer: null }` would resurrect the customer as `{ transactionalSMSOptIn: false }` and fail every
 * sibling's required check. A submitted-as-null ancestor means the whole branch was intentionally omitted.
 */
function hasExplicitlyNullAncestor(obj: { [key: string]: any }, path: string) {
  const ancestorSegments = path.split('.').slice(0, -1)
  return ancestorSegments.some((_, index) => get(obj, ancestorSegments.slice(0, index + 1)) === null)
}

function resolveFileKeys(entries: (readonly [string, any])[]) {
  const fileArrayKeys = entries
    .filter(([key, value]) => Array.isArray(value) && value[0] instanceof File)
    .map(([key, value]: readonly [string, File[]]) => {
      const subKeys = entries
        .filter(([subKey]) => subKey !== key && subKey.startsWith(key))
        .map(([subKey, value]) => ({ key: subKey, value: value, index: Number(subKey.replace(`${key}.`, '')) }))
      const newArr: (File | string)[] = [...value]
      subKeys.forEach(({ index, value }) => newArr.splice(index, 0, value))
      const fileKeys = newArr
        .map((f, i) => ({ key: `${key}.${i}`, value: f }))
        .filter((f) => f.value instanceof File)
        .map((f) => f.key)

      return {
        key,
        value: newArr,
        fileKeys,
        keysToRemove: [key, ...subKeys.map((s) => s.key)]
      }
    })
  const allKeysToRemove = fileArrayKeys.flatMap((f) => f.keysToRemove)
  const newEntries = [
    ...entries.filter(([key]) => !allKeysToRemove.includes(key)),
    ...fileArrayKeys.map((k) => [k.key, k.value] as [string, (string | File)[]])
  ]
  const allFileKeys = [
    ...fileArrayKeys.flatMap((f) => f.fileKeys),
    ...entries.filter(([key, value]) => value instanceof File).map(([key]) => key)
  ]
  return { entries: newEntries, fileKeys: [...new Set(allFileKeys)] }
}

export function parseFormValue(schema: AnyZodSchema | undefined, formValue: FormDataEntryValue[]) {
  try {
    const allowNull = schema ? z.safeParse(schema, null).success : false
    const allowUndefined = schema ? z.safeParse(schema, undefined).success : false
    const unwrapped = schema ? unwrapEffects(schema) : null
    if (formValue.length && formValue.every((f) => f instanceof File)) {
      if (formValue.every((f) => f instanceof File && f.name === '' && f.size === 0)) {
        if (unwrapped instanceof z.ZodArray) {
          return []
        } else {
          return null
        }
      } else if (schema && (unwrapped instanceof z.ZodArray || unwrapped instanceof z.ZodTuple)) {
        return formValue
      } else {
        return formValue[0]
      }
    } else if (!schema) {
      return (formValue.length === 1 ? formValue[0] : formValue) as never
    } else if (unwrapped instanceof z.ZodArray) {
      const rawValue = formValue as string[]
      if (rawValue.length === 1) {
        if (rawValue[0] === '') {
          // for multiple file inputs, when no files are selected, an empty string is submitted.
          return []
        }
        try {
          const parsed = JSON.parse(rawValue[0])
          if (Array.isArray(parsed)) {
            return parsed
          }
        } catch {
          // do nothing
        }
      }

      if (rawValue.length === 0 && allowNull) {
        return null
      } else if (rawValue.length === 0 && allowUndefined) {
        return null
      } else {
        const value = rawValue.map((v) => parseValueHelper(v, unwrapped.element))
        return value
      }
    } else if (unwrapped instanceof z.ZodTuple) {
      const rawValue = formValue as string[]
      const value = rawValue.map((v, i) => {
        const schema = i >= unwrapped.def.items.length ? unwrapped.def.items[i] : (unwrapped.def.rest ?? z.any())
        return parseValueHelper(v, schema)
      })
      return value
    } else {
      const rawValue = formValue[0] as string
      const value = parseValueHelper(rawValue, schema)
      return value
    }
  } catch {
    return null as never
  }
}

function parseValueHelper(value: string, schema: AnyZodSchema) {
  const base = unwrapEffects(schema)

  if (value === '' && z.safeParse(schema, null).success) {
    return null
  } else if (base instanceof z.ZodNumber) {
    return value === '' ? null : Number(value)
  } else if (base instanceof z.ZodBoolean) {
    return value === 'true'
  } else if (base instanceof z.ZodString) {
    return value
  } else {
    try {
      const result = z.parse(schema, JSON.parse(value))
      return result
    } catch {
      return value ? value : null
    }
  }
}

function unwrapEffects(schema: AnyZodSchema): AnyZodSchema {
  if (schema instanceof z.ZodNullable) {
    return unwrapEffects(schema.unwrap())
  } else if (schema instanceof z.ZodOptional) {
    return unwrapEffects(schema.unwrap())
  } else if (schema instanceof z.ZodPipe) {
    return unwrapEffects(schema.in)
  } else {
    return schema
  }
}

function resolveSchemaForFormKey(flat: FlattenedField[], formKey: string, formData: FormData) {
  const candidates = flat.filter((f) => formPathsMatch(formKey, f.path))
  if (candidates.length < 2) {
    return candidates[0]?.schema
  }

  const branchesMatchingSubmittedDiscriminators = candidates.filter((candidate) =>
    candidate.discriminators.every((discriminator) => discriminatorIsSatisfied(discriminator, formKey, formData))
  )

  // A submitted discriminator that matches no branch is the user's problem, not ours — coerce with any branch
  // and let the schema report the invalid discriminator instead of throwing here. Zod guarantees discriminator
  // values are distinct across branches, so a match is never ambiguous.
  return (branchesMatchingSubmittedDiscriminators[0] ?? candidates[0]).schema
}

function formPathsMatch(path1: string, path2: string) {
  const segments1 = path1.split('.')
  const segments2 = path2.split('.')
  return (
    segments1.length === segments2.length &&
    segments1.every((s1, i) => s1 === '*' || segments2[i] === '*' || s1 === segments2[i])
  )
}

function flattenZodSchema(schema: AnyZodSchema): FlattenedField[] {
  return flattenZodSchemaHelper(schema)
}

function flattenZodSchemaHelper(
  schema: AnyZodSchema,
  prefix = '',
  inheritedDiscriminators: DiscriminatorConstraint[] = []
): FlattenedField[] {
  function getPath(path: string) {
    return prefix ? `${prefix}.${path}` : path
  }
  return innerTypes(schema).flatMap((innerTypes) => {
    const path = getPath(innerTypes.path)
    const discriminators = [
      ...inheritedDiscriminators,
      ...innerTypes.discriminators.map((d) => ({ ...d, path: getPath(d.path) }))
    ]
    return [
      { path, schema: innerTypes.schema, discriminators },
      ...flattenZodSchemaHelper(innerTypes.schema, path, discriminators)
    ]
  })
}

function innerTypes(schema: AnyZodSchema): InnerType[] {
  if (schema instanceof z.core.$ZodArray) {
    return [{ path: '*', schema: schema._zod.def.element, discriminators: [] }]
  } else if (schema instanceof z.core.$ZodObject) {
    return Object.entries(schema._zod.def.shape).map(([path, schema]) => ({ path, schema, discriminators: [] }))
  }
  // $ZodDiscriminatedUnion extends $ZodUnion, so it has to be checked first or it never reaches its own branch
  else if (schema instanceof z.core.$ZodDiscriminatedUnion) {
    const discriminatorKey = schema._zod.def.discriminator
    return schema._zod.def.options.flatMap((option) => {
      const allowedValues = discriminatorValuesForOption(option, discriminatorKey)
      return innerTypes(option).map((inner) => ({
        ...inner,
        discriminators: [{ path: discriminatorKey, allowedValues }, ...inner.discriminators]
      }))
    })
  } else if (schema instanceof z.core.$ZodUnion) {
    return schema._zod.def.options.flatMap((o) => innerTypes(o))
  } else if (schema instanceof z.core._intersection) {
    return []
  } else if (schema instanceof z.core.$ZodTuple) {
    return [
      ...schema._zod.def.items.map((item: AnyZodSchema, index: number) => ({
        path: index.toString(),
        schema: item,
        discriminators: []
      })),
      ...(schema._zod.def.rest ? [{ path: '*', schema: schema._zod.def.rest, discriminators: [] }] : [])
    ]
  } else if (schema instanceof z.core.$ZodRecord) {
    return [{ path: '*', schema: schema._zod.def.valueType, discriminators: [] }]
  } else if (schema instanceof z.core.$ZodLazy) {
    throw new Error('Cannot process ZodLazy')
  } else if (schema instanceof z.core.$ZodPipe) {
    return innerTypes(schema._zod.def.in)
  } else if (schema instanceof z.core.$ZodOptional) {
    return innerTypes(schema._zod.def.innerType)
  } else if (schema instanceof z.core.$ZodNullable) {
    return innerTypes(schema._zod.def.innerType)
  } else if (schema instanceof z.core.$ZodDefault) {
    return innerTypes(schema._zod.def.innerType)
  } else if (schema instanceof z.core.$ZodCatch) {
    return innerTypes(schema._zod.def.innerType)
  } else if (schema instanceof z.core.$ZodStringFormat) {
    return []
  } else if (schema instanceof z.core.$ZodString) {
    return []
  } else if (schema instanceof z.core.$ZodNumber) {
    return []
  } else if (schema instanceof z.core.$ZodBoolean) {
    return []
  } else {
    return []
  }
}

function discriminatorIsSatisfied(discriminator: DiscriminatorConstraint, formKey: string, formData: FormData) {
  const submitted = formData.get(concreteFormPath(discriminator.path, formKey))
  if (typeof submitted !== 'string') {
    return false
  }
  return [...discriminator.allowedValues].some((allowed) => String(allowed) === submitted)
}

/**
 * Flattened paths use `*` for array indices, so `conditions.*.field` has to be resolved against the index of the
 * key being parsed — `conditions.1.value` must read its discriminator from `conditions.1.field`, not `conditions.0.field`.
 */
function concreteFormPath(templatePath: string, formKey: string) {
  const templateSegments = templatePath.split('.')
  const keySegments = formKey.split('.')
  return templateSegments.map((segment, i) => (segment === '*' ? (keySegments[i] ?? segment) : segment)).join('.')
}

/**
 * Throws rather than returning nothing, because an unconstrained branch satisfies every submission
 * (`[].every()` is `true`) and would then win the branch race for any form key — silently restoring the
 * first-branch-wins coercion this resolution exists to replace.
 */
function discriminatorValuesForOption(option: AnyZodSchema, discriminatorKey: string): Set<unknown> {
  const propValues = (option._zod as { propValues?: { [key: string]: Set<unknown> } }).propValues?.[discriminatorKey]
  if (propValues?.size) {
    return propValues
  }

  const shape = (option._zod.def as { shape?: { [key: string]: AnyZodSchema } }).shape
  const literalValues = shape?.[discriminatorKey]?._zod.values
  if (literalValues?.size) {
    return literalValues
  }

  throw new Error(`Cannot read the values of discriminator '${discriminatorKey}' on a discriminated union branch`)
}

type AnyZodSchema = z.core.$ZodType<unknown, unknown, z.core.$ZodTypeInternals<unknown, unknown>>

type DiscriminatorConstraint = { path: string; allowedValues: Set<unknown> }

type FlattenedField = { path: string; schema: AnyZodSchema; discriminators: DiscriminatorConstraint[] }

type InnerType = { path: string; schema: AnyZodSchema; discriminators: DiscriminatorConstraint[] }
